<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { ArrowRight, Info, LoaderCircle, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-vue-next'
import { Button } from '@/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/ui/alert-dialog'
import RecoveryExceptionModal from './RecoveryExceptionModal.vue'
import RecoveryMethodPicker from './RecoveryMethodPicker.vue'
import { getMethodMeta } from './methods'
import { useRecoveryConfig, type RecoveryException } from './useRecoveryConfig'

const { t } = useI18n()
const { stores } = useLocalStorage()
const {
  defaultSelection,
  onlineUrl,
  exceptions,
  isLoading,
  loadFailed,
  isOnlineUsedAnywhere,
  upsertException,
  removeException,
  newException,
  cloneSelection,
  load,
  ensureLoaded,
  autoSave
} = useRecoveryConfig()

const isMonoStore = computed(() => stores.value.length <= 1)

onMounted(async () => {
  await ensureLoaded()

  if (isMonoStore.value) {
    const storeId = stores.value[0]?.id
    const ownException = storeId != null ? exceptions.value.find((e) => e.storeIds.includes(storeId)) : undefined

    if (ownException) defaultSelection.methods = [...ownException.selection.methods]

    exceptions.value = []
  }
})

watch(isOnlineUsedAnywhere, (used) => {
  if (!used) onlineUrl.value = ''
})

watchDebounced([defaultSelection, exceptions, onlineUrl], () => autoSave(), { debounce: 700, deep: true })

const storeName = (id: number) => stores.value.find((s) => s.id === id)?.name ?? `#${id}`

const summarizeMethods = (exc: RecoveryException) =>
  exc.selection.methods.map((key) => ({
    icon: getMethodMeta(key).icon,
    label: t(`pages.loyaltyPage.tabs.recovery.methods.${key}.label`)
  }))

const modalOpen = ref(false)
const isEditing = ref(false)
const draft = ref<RecoveryException>(newException())

const cloneException = (exc: RecoveryException): RecoveryException => ({
  id: exc.id,
  storeIds: [...exc.storeIds],
  selection: cloneSelection(exc.selection)
})

const openAdd = () => {
  draft.value = newException()
  isEditing.value = false
  modalOpen.value = true
}

const openEdit = (exc: RecoveryException) => {
  draft.value = cloneException(exc)
  isEditing.value = true
  modalOpen.value = true
}

const onSave = () => {
  upsertException(cloneException(draft.value))
  modalOpen.value = false
}

const deleteDialogOpen = ref(false)
const pendingDeleteId = ref<string | null>(null)

const askRemoveException = (id: string) => {
  pendingDeleteId.value = id
  deleteDialogOpen.value = true
}

const confirmRemoveException = () => {
  if (pendingDeleteId.value) removeException(pendingDeleteId.value)
  pendingDeleteId.value = null
  deleteDialogOpen.value = false
}
</script>

<template>
  <TabSubPanel
    :section-main-title="t('pages.loyaltyPage.tabs.recovery.sectionTitle')"
    :section-main-subtitle="t('pages.loyaltyPage.tabs.recovery.sectionSubtitle')"
  >
    <template #headerAction>
      <BaseChip :text="t('global.words.assistance')" color="blue" size="sm" />
    </template>

    <template #content>
      <div v-if="isLoading" class="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <LoaderCircle class="size-4 animate-spin" />
        {{ t('global.words.loading') }}
      </div>

      <div v-else-if="loadFailed" class="flex flex-col items-center gap-3 py-10 text-center">
        <p class="text-sm text-muted-foreground">{{ t('pages.loyaltyPage.tabs.recovery.notifications.loadError') }}</p>
        <Button variant="outline" size="sm" @click="load">
          <RefreshCw />
          {{ t('global.words.refresh') }}
        </Button>
      </div>

      <template v-else>
        <template v-if="isMonoStore">
          <RecoveryMethodPicker v-model="defaultSelection" />
        </template>

        <template v-else>
          <div class="flex items-center gap-1.5">
            <h4 class="text-sm font-semibold text-foreground">
              {{ t('pages.loyaltyPage.tabs.recovery.defaultMethodsTitle') }}
            </h4>
            <AppTooltip :text="t('pages.loyaltyPage.tabs.recovery.defaultMethodsTooltip')">
              <span
                class="inline-flex cursor-help text-muted-foreground"
                tabindex="0"
                :aria-label="t('pages.loyaltyPage.tabs.recovery.defaultMethodsAria')"
              >
                <Info class="size-4" />
              </span>
            </AppTooltip>
          </div>
          <div class="mt-4">
            <RecoveryMethodPicker v-model="defaultSelection" />
          </div>

          <div class="mt-8 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1.5">
                <h4 class="text-sm font-semibold text-foreground">
                  {{ t('pages.loyaltyPage.tabs.recovery.exceptionsTitle') }}
                </h4>
                <AppTooltip :text="t('pages.loyaltyPage.tabs.recovery.exceptionsTooltip')">
                  <span
                    class="inline-flex cursor-help text-muted-foreground"
                    tabindex="0"
                    :aria-label="t('pages.loyaltyPage.tabs.recovery.exceptionsAria')"
                  >
                    <Info class="size-4" />
                  </span>
                </AppTooltip>
              </div>
              <Button variant="outline" size="sm" @click="openAdd">
                <Plus />
                {{ t('pages.loyaltyPage.tabs.recovery.addException') }}
              </Button>
            </div>

            <p
              v-if="exceptions.length === 0"
              class="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground"
            >
              {{ t('pages.loyaltyPage.tabs.recovery.noException') }}
            </p>

            <ul v-else class="space-y-2">
              <li
                v-for="exc in exceptions"
                :key="exc.id"
                class="flex items-center gap-3 rounded-md border border-border p-3"
              >
                <div class="flex shrink-0 items-center gap-1.5">
                  <template v-if="exc.storeIds.length <= 3">
                    <Badge v-for="id in exc.storeIds" :key="id" variant="secondary">{{ storeName(id) }}</Badge>
                  </template>
                  <AppTooltip v-else :text="exc.storeIds.map(storeName).join(', ')">
                    <Badge variant="secondary" class="cursor-help">
                      {{
                        t(
                          'pages.loyaltyPage.tabs.recovery.storesCount',
                          { count: exc.storeIds.length },
                          exc.storeIds.length
                        )
                      }}
                    </Badge>
                  </AppTooltip>
                </div>

                <ArrowRight class="size-4 shrink-0 text-muted-foreground" />

                <div class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                  <Badge v-for="m in summarizeMethods(exc)" :key="m.label" variant="outline" class="gap-1 font-normal">
                    <component :is="m.icon" class="size-3.5 text-muted-foreground" />
                    {{ m.label }}
                  </Badge>
                </div>

                <div class="ml-auto flex shrink-0 gap-1">
                  <Button variant="ghost" size="icon-sm" @click="openEdit(exc)"><Pencil /></Button>
                  <Button variant="ghost" size="icon-sm" @click="askRemoveException(exc.id)"><Trash2 /></Button>
                </div>
              </li>
            </ul>
          </div>
        </template>
      </template>
    </template>
  </TabSubPanel>

  <RecoveryExceptionModal
    v-model:draft="draft"
    :is-open="modalOpen"
    :is-editing="isEditing"
    @close="modalOpen = false"
    @save="onSave"
  />

  <AlertDialog v-model:open="deleteDialogOpen">
    <AlertDialogContent class="sm:max-w-sm">
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('pages.loyaltyPage.tabs.recovery.deleteException.title') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('pages.loyaltyPage.tabs.recovery.deleteException.text') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{{ t('global.words.cancel') }}</AlertDialogCancel>
        <AlertDialogAction @click="confirmRemoveException">{{ t('global.words.delete') }}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
